import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AlarmChip from '@sophocles/components/AlarmChip';
import StatusChip from '@sophocles/components/StatusChip';
import { selectAlarmDefinitions } from '@sophocles/features/reference';
import {
  extractLoadDetails,
  formatDateTime,
  formatDaysOfWeek,
  formatScheduleTime,
  getAlarmSummary,
  getScheduleComplianceSummary,
} from '@sophocles/features/reports/utils/scheduleComplianceHelpers';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';

/**
 * Displays detailed schedule compliance information in an expandable row
 */
const ScheduleComplianceDetails = ({
  report,
  hideStats = false,
  hidePanelTitle = false,
  isStaff = false,
  isBillboard = true,
}) => {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const [expandedStaffInfo, setExpandedStaffInfo] = useState({});
  const alarmDefinitions = useSelector(selectAlarmDefinitions);
  const { singular: panelZoneLabel, plural: panelZoneLabelPlural } = useRelayLoadTerm(
    isBillboard
      ? { singularKey: 'reports.panel', pluralKey: 'reports.panels' }
      : { singularKey: 'reports.zone', pluralKey: 'reports.zones' },
  );

  // Get user from Redux store for formatting preferences
  const user = useSelector(state => state.auth?.get('user'));

  const loadDetails = extractLoadDetails(report);
  const summary = getScheduleComplianceSummary(report);

  if (!summary) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">
          {t('reports.noComplianceData', 'No schedule compliance data available')}
        </Typography>
      </Box>
    );
  }

  // Group schedules by relay AND panel/load/zone (don't mix relays)
  const groupedByLoad = loadDetails.reduce((acc, load) => {
    const key = `${load.relayNumber}_${load.loadNames}`;
    if (!acc[key]) {
      acc[key] = {
        loadNames: load.loadNames,
        loadIds: load.loadIds,
        relayNumber: load.relayNumber,
        schedules: [],
        status: 'passed', // Will be set to failed if any schedule fails
        allAlarms: [],
      };
    }
    acc[key].schedules.push(load);
    if (load.status === 'failed') {
      acc[key].status = 'failed';
    }
    acc[key].allAlarms.push(...load.alarms);
    return acc;
  }, {});

  // Calculate unique alarm counts after grouping
  const groupedLoads = Object.values(groupedByLoad).map(group => {
    // Get unique alarms by alarm_id and raised_at
    const uniqueAlarms = new Map();
    group.allAlarms.forEach(alarm => {
      const key = `${alarm.alarm_id}_${alarm.raised_at}`;
      if (!uniqueAlarms.has(key)) {
        uniqueAlarms.set(key, alarm);
      }
    });

    // Count failing vs informational
    let failingCount = 0;
    let informationalCount = 0;
    uniqueAlarms.forEach(alarm => {
      const isFailing = alarm.fails_operational_report !== false;
      if (isFailing) {
        failingCount++;
      } else {
        informationalCount++;
      }
    });

    return {
      ...group,
      failingAlarmCount: failingCount,
      informationalAlarmCount: informationalCount,
    };
  });

  const getStatusIcon = status => {
    switch (status?.toLowerCase()) {
      case 'passed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <WarningIcon color="warning" fontSize="small" />;
    }
  };

  const getAlarmChip = alarm => {
    const alarmDetails = alarmDefinitions.find(a => a.key === alarm.shortName);
    const translationKey = `alarms.${alarm.shortName}`;
    const alarmName = alarmDetails
      ? tAlarm(translationKey, { defaultValue: alarmDetails.display_name ?? alarm.displayName })
      : alarm.displayName;

    return (
      <AlarmChip
        key={alarm.id}
        label={alarmName}
        color={alarm.isInformational ? 'info' : 'bad'}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
      {/* Summary Section */}
      {!hideStats && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {panelZoneLabelPlural}
              </Typography>
              <Typography variant="h6">{groupedLoads.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('reports.passed', 'Passed')}
              </Typography>
              <Typography variant="h6" color="success.main">
                {groupedLoads.filter(l => l.status === 'passed').length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('reports.failed', 'Failed')}
              </Typography>
              <Typography variant="h6" color="error.main">
                {groupedLoads.filter(l => l.status === 'failed').length}
              </Typography>
            </Box>
            {summary.scheduleChangesDetected && (
              <Chip
                icon={<WarningIcon />}
                label={t('reports.scheduleChangesDetected', 'Schedule changes detected during report period')}
                color="warning"
                size="small"
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Load Details Section */}
      {groupedLoads.length === 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">{t('reports.noLoadDetails', 'No load details available')}</Typography>
        </Paper>
      ) : (
        <Box>
          {groupedLoads.map((group, index) => {
            // Create a map of alarms with their affected schedules and schedule-specific durations
            const alarmScheduleMap = new Map();

            group.schedules.forEach(schedule => {
              const alarms = getAlarmSummary(schedule.alarms);
              alarms.forEach(alarm => {
                const key = `${alarm.alarmId}_${alarm.raisedAt}`;
                if (!alarmScheduleMap.has(key)) {
                  alarmScheduleMap.set(key, {
                    ...alarm,
                    affectedSchedules: [],
                    scheduleDurations: {}, // Track duration per schedule
                  });
                }
                const alarmEntry = alarmScheduleMap.get(key);
                alarmEntry.affectedSchedules.push(schedule.scheduleNumber);
                // Store the duration for this specific schedule
                alarmEntry.scheduleDurations[schedule.scheduleNumber] = alarm.durationInWindow;
              });
            });

            const allAlarms = Array.from(alarmScheduleMap.values()).map(alarm => ({
              ...alarm,
              // Calculate total unique duration across all affected schedules
              totalDuration: alarm.durationInWindow, // We'll show individual schedule durations in the cell
            }));

            // Calculate total scheduled runtime from backend's pre-calculated totals
            // Backend already accounts for state validity, overlaps, and timezone issues
            const totalScheduledSeconds = group.schedules.reduce((sum, schedule) => {
              const scheduleData = report.items?.find(
                item => item.relay_number === schedule.relayNumber && item.schedule_number === schedule.scheduleNumber,
              );
              const runtime = scheduleData?.metadata?.total_schedule_runtime_seconds || 0;
              return sum + runtime;
            }, 0);

            const totalScheduledMinutes = Math.round(totalScheduledSeconds / 60);

            // Calculate total alarm duration and actual runtime from backend data
            let totalAlarmSeconds = 0;
            let totalActualRuntimeSeconds = 0;

            group.schedules.forEach(schedule => {
              const scheduleData = report.items?.find(item => {
                return item.relay_number === schedule.relayNumber && item.schedule_number === schedule.scheduleNumber;
              });

              if (scheduleData?.data?.states) {
                scheduleData.data.states.forEach(state => {
                  // Sum total alarm duration (all alarms, not just failing)
                  const alarmDurationSecs = state.total_alarm_duration_seconds || 0;
                  totalAlarmSeconds += alarmDurationSecs;

                  // Sum actual runtime (scheduled minus alarms)
                  const actualRuntimeSecs = state.estimated_runtime_without_alarms_seconds || 0;
                  totalActualRuntimeSeconds += actualRuntimeSecs;
                });
              }
            });

            const totalAlarmMinutes = Math.round(totalAlarmSeconds / 60);
            const totalActualRuntimeMinutes = Math.round(totalActualRuntimeSeconds / 60);

            const formatMinutes = minutes => {
              const h = Math.floor(minutes / 60);
              const m = minutes % 60;
              return `${h}h ${m}m`;
            };

            return (
              <Accordion key={`${group.relayNumber}_${group.loadNames}`} defaultExpanded expanded>
                {!hidePanelTitle && (
                  <AccordionSummary>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {getStatusIcon(group.status)}
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                        {group.loadNames}
                      </Typography>
                      <StatusChip
                        label={t(
                          `operationalReports.status${group.status.charAt(0).toUpperCase()}${group.status.slice(1)}`,
                        )}
                        color={group.status === 'passed' ? 'ok' : 'bad'}
                      />
                    </Box>
                  </AccordionSummary>
                )}
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Schedule Information */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('reports.scheduleInformation', 'Schedule Information')}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableBody>
                            {group.schedules.map(schedule => {
                              // Helper to format field values for display
                              const formatFieldValue = (field, value) => {
                                if (value === null || value === undefined) return 'null';
                                if (field.includes('time') && typeof value === 'string') {
                                  // Format time fields - remove microseconds
                                  return value.split('.')[0];
                                }
                                if (field.includes('offset')) {
                                  return `${value}m`;
                                }
                                return String(value);
                              };

                              // Build tooltip content for schedule changes
                              const changeTooltip =
                                schedule.changeInfo && schedule.changeInfo.length > 0 ? (
                                  <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                      {t('operationalReports.scheduleChanged', {
                                        defaultValue: 'Schedule changed during this day',
                                      })}
                                    </Typography>
                                    {schedule.changeInfo.map((change, idx) => (
                                      <Box key={idx} sx={{ mt: 1, mb: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                          <strong>State {change.stateNumber}</strong> -{' '}
                                          {formatDateTime(change.changedAt, user)}
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                          {t('operationalReports.changedBy', { defaultValue: 'Changed by' })}:{' '}
                                          {change.changedBy}
                                        </Typography>
                                        {change.fieldChanges.map((fc, fcIdx) => (
                                          <Box key={fcIdx} sx={{ ml: 1, mt: 0.5 }}>
                                            <Typography variant="caption" display="block">
                                              <strong>{fc.field}:</strong>{' '}
                                              <span
                                                style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}
                                              >
                                                {formatFieldValue(fc.field, fc.oldValue)}
                                              </span>
                                              {' → '}
                                              <span style={{ color: 'var(--status-ok)', fontWeight: 'bold' }}>
                                                {formatFieldValue(fc.field, fc.newValue)}
                                              </span>
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    ))}
                                  </Box>
                                ) : null;

                              return (
                                <TableRow key={schedule.id}>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <strong
                                        style={{
                                          opacity: schedule.infoCodes?.includes('SCHEDULE_NOT_ACTIVE_THIS_DAY')
                                            ? 0.5
                                            : 1,
                                        }}
                                      >
                                        Schedule {schedule.scheduleNumber}
                                      </strong>
                                      {schedule.changeInfo && schedule.changeInfo.length > 0 && (
                                        <Tooltip title={changeTooltip} arrow>
                                          <EditIcon fontSize="small" color="warning" />
                                        </Tooltip>
                                      )}
                                      {schedule.infoCodes?.includes('SCHEDULE_NOT_ACTIVE_THIS_DAY') && (
                                        <Tooltip
                                          title={t('operationalReports.scheduleNotActiveThisDay', {
                                            defaultValue: 'Schedule not configured to run on this day',
                                          })}
                                          arrow
                                        >
                                          <StatusChip
                                            label={t('operationalReports.notActive', { defaultValue: 'Not Active' })}
                                            color="neutral"
                                            sx={{ opacity: 0.6 }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      opacity: schedule.infoCodes?.includes('SCHEDULE_NOT_ACTIVE_THIS_DAY') ? 0.5 : 1,
                                    }}
                                  >
                                    {(() => {
                                      // Check if schedule is "always on" using the flag from backend
                                      const isAlwaysOn = schedule.alwaysOn;

                                      if (isAlwaysOn) {
                                        return (
                                          <>
                                            <Box>
                                              {t('operationalReports.alwaysOn', { defaultValue: 'Always On' })}
                                              {schedule.currentStateRuntime && (
                                                <strong> ({schedule.currentStateRuntime})</strong>
                                              )}
                                            </Box>
                                            {schedule.daysOfWeek != null && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                mt={0.5}
                                              >
                                                {formatDaysOfWeek(schedule.daysOfWeek, t)}
                                              </Typography>
                                            )}
                                          </>
                                        );
                                      }

                                      if (schedule.onTime && schedule.offTime) {
                                        return (
                                          <>
                                            <Box>
                                              {formatScheduleTime(
                                                schedule.onTime,
                                                schedule.onType,
                                                schedule.onOffset,
                                                user,
                                                t,
                                              )}
                                              {' - '}
                                              {formatScheduleTime(
                                                schedule.offTime,
                                                schedule.offType,
                                                schedule.offOffset,
                                                user,
                                                t,
                                              )}{' '}
                                              {schedule.scheduleSkipped ? (
                                                <Tooltip
                                                  title={t('operationalReports.scheduleSkipped', {
                                                    defaultValue:
                                                      'Schedule was configured for this time, but the change occurred after this window had already passed',
                                                  })}
                                                  arrow
                                                >
                                                  <strong style={{ color: 'var(--text-muted)' }}>(skipped)</strong>
                                                </Tooltip>
                                              ) : schedule.currentStateRuntime ? (
                                                <strong>({schedule.currentStateRuntime})</strong>
                                              ) : null}
                                            </Box>
                                            {schedule.daysOfWeek != null && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                mt={0.5}
                                              >
                                                {formatDaysOfWeek(schedule.daysOfWeek, t)}
                                              </Typography>
                                            )}
                                          </>
                                        );
                                      }

                                      return (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                          {t('operationalReports.scheduleDidNotRun', {
                                            defaultValue: 'Schedule did not run',
                                          })}
                                        </Typography>
                                      );
                                    })()}
                                    {/* Staff-only info codes section - appears below schedule times */}
                                    {isStaff && (schedule.infoCodes?.length > 0 || schedule.errorCodes?.length > 0) && (
                                      <Box sx={{ mt: 1 }}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                          <Tooltip
                                            title={
                                              expandedStaffInfo[schedule.id]
                                                ? 'Hide technical details'
                                                : t('operationalReports.staffOnlyInfoCodes', {
                                                    defaultValue: 'Staff Info (Technical Details)',
                                                  })
                                            }
                                            arrow
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                setExpandedStaffInfo(prev => ({
                                                  ...prev,
                                                  [schedule.id]: !prev[schedule.id],
                                                }))
                                              }
                                              sx={{
                                                p: 0.25,
                                                color: 'info.main',
                                              }}
                                            >
                                              <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Typography variant="caption" color="info.main" sx={{ fontWeight: 'medium' }}>
                                            {t('reports.staffDetails', 'Staff Details')}
                                          </Typography>
                                        </Box>
                                        <Collapse in={expandedStaffInfo[schedule.id]}>
                                          <Box
                                            sx={{
                                              mt: 0.5,
                                              p: 1,
                                              bgcolor: 'info.50',
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'info.main',
                                            }}
                                          >
                                            {schedule.infoCodes?.length > 0 && (
                                              <Box sx={{ mb: 0.5 }}>
                                                <Typography variant="caption" display="block">
                                                  <strong>{t('reports.infoCodes', 'Info Codes:')}</strong>
                                                </Typography>
                                                {schedule.infoCodes.map((code, idx) => (
                                                  <Box key={idx} sx={{ ml: 1, mb: 0.5 }}>
                                                    <Typography variant="caption" display="block">
                                                      • {code}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      display="block"
                                                      sx={{ ml: 1 }}
                                                    >
                                                      {t(`operationalReports.infoCode${code}`, {
                                                        defaultValue: code.replace(/_/g, ' '),
                                                      })}
                                                    </Typography>
                                                    {t(`operationalReports.infoCode${code}_description`, {
                                                      defaultValue: '',
                                                    }) && (
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ ml: 1 }}
                                                      >
                                                        {t(`operationalReports.infoCode${code}_description`, {
                                                          defaultValue: '',
                                                        })}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                            {schedule.errorCodes?.length > 0 && (
                                              <Box sx={{ mb: 0.5 }}>
                                                <Typography variant="caption" display="block">
                                                  <strong>{t('reports.errorCodes', 'Error Codes:')}</strong>
                                                </Typography>
                                                {schedule.errorCodes.map((code, idx) => (
                                                  <Box key={idx} sx={{ ml: 1, mb: 0.5 }}>
                                                    <Typography variant="caption" display="block">
                                                      • {code}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      display="block"
                                                      sx={{ ml: 1 }}
                                                    >
                                                      {t(`operationalReports.failureCode${code}`, {
                                                        defaultValue: code.replace(/_/g, ' '),
                                                      })}
                                                    </Typography>
                                                    {t(`operationalReports.failureCode${code}_description`, {
                                                      defaultValue: '',
                                                    }) && (
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ ml: 1 }}
                                                      >
                                                        {t(`operationalReports.failureCode${code}_description`, {
                                                          defaultValue: '',
                                                        })}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                            {schedule.failureReason && (
                                              <Box>
                                                <Typography variant="caption" display="block">
                                                  <strong>{t('reports.failureReason', 'Failure Reason:')}</strong>
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  display="block"
                                                  sx={{ fontStyle: 'italic' }}
                                                >
                                                  {schedule.failureReason}
                                                </Typography>
                                              </Box>
                                            )}
                                            {schedule.hasDeploymentMismatch && schedule.deployedConfig && (
                                              <Box
                                                sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
                                              >
                                                <Typography variant="caption" display="block" color="warning.main">
                                                  <strong>⚠️ Config Mismatch Detected</strong>
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  display="block"
                                                  color="text.secondary"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  {t(
                                                    'reports.scheduleMismatch',
                                                    'Device is running different schedule than expected:',
                                                  )}
                                                </Typography>
                                                <Box sx={{ ml: 1 }}>
                                                  <Typography variant="caption" display="block">
                                                    <strong>{t('reports.deployedActual', 'Deployed (Actual):')}</strong>
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                    sx={{ ml: 1 }}
                                                  >
                                                    {schedule.deployedConfig.alwaysOn
                                                      ? 'Always On'
                                                      : `${formatScheduleTime(
                                                          schedule.deployedConfig.onTime,
                                                          schedule.deployedConfig.onType,
                                                          schedule.deployedConfig.onOffset,
                                                          user,
                                                          t,
                                                        )} - ${formatScheduleTime(
                                                          schedule.deployedConfig.offTime,
                                                          schedule.deployedConfig.offType,
                                                          schedule.deployedConfig.offOffset,
                                                          user,
                                                          t,
                                                        )}`}
                                                  </Typography>
                                                  {schedule.deployedConfig.daysOfWeek && (
                                                    <Typography
                                                      variant="caption"
                                                      display="block"
                                                      color="text.secondary"
                                                      sx={{ ml: 1 }}
                                                    >
                                                      {formatDaysOfWeek(schedule.deployedConfig.daysOfWeek, t)}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            )}
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow>
                              <TableCell>
                                <strong>
                                  {t('operationalReports.scheduledRuntime', {
                                    defaultValue: 'Scheduled Runtime',
                                  })}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>{formatMinutes(totalScheduledMinutes)}</strong>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <strong>
                                  {t('operationalReports.alarmDuration', { defaultValue: 'Alarm Duration' })}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>{formatMinutes(totalAlarmMinutes)}</strong>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <strong>
                                  {t('operationalReports.actualRuntime', { defaultValue: 'Actual Runtime' })}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>{formatMinutes(totalActualRuntimeMinutes)}</strong>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Alarm Information */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Alarms During Schedule Window ({allAlarms.length})
                      </Typography>
                      {allAlarms.length === 0 ? (
                        <Box
                          sx={{
                            p: 2,
                            border: '1px dashed',
                            borderColor: 'success.main',
                            borderRadius: 1,
                            bgcolor: 'success.50',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircleIcon color="success" />
                            <Typography color="success.main">
                              {t('reports.noAlarmsDuringSchedule', 'No alarms during schedule')}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('reports.alarm', 'Alarm')}</TableCell>
                                <TableCell>Raised</TableCell>
                                <TableCell>Schedule</TableCell>
                                {/* <TableCell>Duration</TableCell> */}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allAlarms.map(alarm => (
                                <TableRow key={alarm.id}>
                                  <TableCell>{getAlarmChip(alarm)}</TableCell>
                                  <TableCell>
                                    <Typography variant="caption">{formatDateTime(alarm.raisedAt, user)}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">{alarm.affectedSchedules.join(', ')}</Typography>
                                  </TableCell>
                                  {/* <TableCell>
                                    {alarm.affectedSchedules.length === 1 ? (
                                      <Typography variant="caption" fontWeight="bold">
                                        {alarm.scheduleDurations[alarm.affectedSchedules[0]] || '-'}
                                      </Typography>
                                    ) : (
                                      <Box>
                                        {alarm.affectedSchedules.map(schedNum => (
                                          <Typography
                                            key={schedNum}
                                            variant="caption"
                                            display="block"
                                            fontWeight="bold"
                                          >
                                            {schedNum}: {alarm.scheduleDurations[schedNum] || '-'}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}
                                  </TableCell> */}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Grid>

                    {/* Failure Reason - Show error codes and failure reasons (only if not alarm-related) */}
                    {group.status === 'failed' &&
                      (() => {
                        // Check if any schedules have non-alarm error codes
                        const schedulesWithErrors = group.schedules.filter(s => {
                          if (!s.errorCodes || s.errorCodes.length === 0) return false;
                          // Include if has any error code that is NOT ALARMS_DETECTED
                          const hasNonAlarmError = s.errorCodes.some(code => code !== 'ALARMS_DETECTED');
                          return hasNonAlarmError;
                        });

                        // Only show this section if there are non-alarm errors
                        if (schedulesWithErrors.length === 0) {
                          return null;
                        }

                        return (
                          <Grid size={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'error.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'error.main',
                              }}
                            >
                              <Typography variant="subtitle2" color="error.main" gutterBottom>
                                {t('operationalReports.failureReason', { defaultValue: 'Failure Reason' })}
                              </Typography>

                              {schedulesWithErrors.map((schedule, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    mb: schedulesWithErrors.length > 1 && idx < schedulesWithErrors.length - 1 ? 2 : 0,
                                  }}
                                >
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Schedule {schedule.scheduleNumber}:</strong>
                                  </Typography>
                                  {schedule.errorCodes
                                    .filter(code => code !== 'ALARMS_DETECTED')
                                    .map((errorCode, codeIdx) => {
                                      const errorLabelKey = `operationalReports.failureCode${errorCode}`;
                                      const errorDescKey = `operationalReports.failureCode${errorCode}_description`;

                                      const errorLabel = t(errorLabelKey, {
                                        defaultValue: errorCode.replace(/_/g, ' '),
                                      });
                                      const errorDescription = t(errorDescKey, { defaultValue: '' });

                                      return (
                                        <Box key={codeIdx} sx={{ ml: 2, mb: 0.5 }}>
                                          <Typography variant="body2">• {errorLabel}</Typography>
                                          {errorDescription && (
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                              {errorDescription}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  {schedule.failureReason && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ ml: 2, fontStyle: 'italic', mt: 0.5 }}
                                    >
                                      {schedule.failureReason}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        );
                      })()}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ScheduleComplianceDetails;
